import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Img, { FluidObject, FixedObject } from 'gatsby-image'
import { Col } from 'antd'
import { Section, SectionRow } from '../../components'

type Props = { data: Data }
interface Data {
  id: string
  title: string
  slug: string
  order: number
  theme: string
  secondaryTheme: string
  gradientTheme: string
  quote: string
  services: Array<string>
  showcaseImage: {
    fluid: FluidObject
  }
  headerImage: {
    fluid: FluidObject
  }
  fullscreenImage: {
    fluid: FluidObject
  }
  x1grid: Array<Both>
  description: {
    description: string
  }
  createdAt: Date
  playStoreLink: string
  appleStoreLink: string
  websiteLink: string
}
interface Both {
  fluid: FluidObject
  fixed: FixedObject
}
//Styled Components
const Hero = styled('section')<{ color: string }>`
  min-height: calc(100vh);
  display: flex;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  background: rgb(255, 255, 255);
  background: ${props =>
    `linear-gradient(
			0deg,
			rgba(255, 255, 255, 1) 0%,
			${props.color} 50%,
			${props.color} 100%
		)`};
`
const HeroImg = styled(Img)`
  max-width: 900px;
`
const WideImg = styled(Img)`
  max-width: 100vw;
`
const ListImg = styled(Img)`
  max-width: 1200px;
`
const CaseStudyTitle = styled('h2')<{ color: string }>`
  color: ${props => `${props.color}`};
  font-size: 36px;
  font-weight: bold;
  letter-spacing: 1px;
  line-height: 62px;
`
const Paragraph = styled.p`
  color: #474747;
  font-size: 18px;
  letter-spacing: 0.3px;
  line-height: 40px;
  text-align: justify;
  margin-bottom: 28px;
  @media screen and (max-width: 768px) {
    font-size: 16px;
    line-height: 32px;
  }
`
const ServicesList = styled.p`
  color: #bbbbbb;
  font-size: 18px;
  letter-spacing: 0.3px;
  line-height: 32px;
  margin-bottom: 8px;
`
const PortfolioButton = styled('button')<{ color: string }>`
  color: ${props => `${props.color}`};
  height: 45px;
  width: 180px;
  font-size: 18px;
  border: ${props => `3px solid ${props.color}`};
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  font-size: 16px;
  margin: 8px 0;
  :hover {
    cursor: pointer;
  }
  :focus {
    outline: 0;
  }
  :hover {
    color: white;
    background-color: ${props => `${props.color}`};
  }
`
//Component
const CaseStudyRoot: React.SFC<Props> = ({ data }) => {
  let mql
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    if (typeof window !== undefined) {
      mql = window.matchMedia(`(min-width: 768px)`)
      mql.addListener(mediaQueryChanged)
      mediaQueryChanged()
    }
    return () => {
      if (typeof window !== undefined) {
        mql.removeListener(mediaQueryChanged)
      }
    }
  }, [])
  const mediaQueryChanged = () => {
    if (mql.matches) {
      setMobile(false)
    } else {
      setMobile(true)
    }
  }
  return (
    <React.Fragment>
      <Hero color={`#${data.theme}`}>
        <div style={{ width: '100%', maxWidth: '900px' }}>
          <HeroImg
            fluid={data.headerImage.fluid}
            alt={`${data.title} - Lattice Studios`}
          />
        </div>
      </Hero>
      <Section>
        <SectionRow gutter={32}>
          <Col
            style={{
              marginTop: '24px',
              marginBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
            }}
            md={7}
            sm={24}
            xs={24}
          >
            <CaseStudyTitle color={`#${data.theme}`}>
              {data.title}
            </CaseStudyTitle>
            {data.websiteLink && (
              <a
                title={`Website - ${data.title}`}
                href={data.websiteLink}
                target="_blank"
                rel="noopener"
              >
                <PortfolioButton color={`#${data.theme}`}>
                  Visit Their Website
                </PortfolioButton>
              </a>
            )}
            {data.playStoreLink && (
              <a
                title={`Play Store - ${data.title}`}
                href={data.playStoreLink}
                target="_blank"
                rel="noopener"
              >
                <PortfolioButton color={`#${data.theme}`}>
                  Play Store
                </PortfolioButton>
              </a>
            )}
            {data.appleStoreLink && (
              <a
                title={`Apple Store - ${data.title}`}
                href={data.appleStoreLink}
                target="_blank"
                rel="noopener"
              >
                <PortfolioButton color={`#${data.theme}`}>
                  App Store
                </PortfolioButton>
              </a>
            )}
          </Col>
          <Col
            style={{ marginTop: '24px', marginBottom: '24px' }}
            md={12}
            sm={24}
            xs={24}
          >
            <Paragraph>{data.description.description}</Paragraph>
          </Col>
          <Col
            style={{ marginTop: '24px', marginBottom: '24px' }}
            md={5}
            sm={24}
            xs={24}
          >
            <ServicesList style={{ fontWeight: 900 }}>Services</ServicesList>
            {data.services.map((service, index) => {
              return <ServicesList key={index}>{service}</ServicesList>
            })}
          </Col>
        </SectionRow>
      </Section>
      <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {data.x1grid.map((image, index) => {
          if (index + 1 === data.x1grid.length / 2) {
            return (
              <li key={index}>
                <div
                  key={index}
                  style={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '24px auto',
                  }}
                >
                  <ListImg
                    fluid={image.fluid}
                    alt={`${index} - ${data.title} - Case Study - Lattice Studios `}
                  />
                </div>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100vw',
                    margin: '24px auto',
                  }}
                >
                  <WideImg
                    fluid={data.fullscreenImage.fluid}
                    alt={`Case Study - Lattice Studios `}
                  />
                </div>
              </li>
            )
          } else {
            return (
              <li
                key={index}
                style={{
                  width: '100%',
                  maxWidth: '1200px',

                  margin: '24px auto',
                }}
              >
                <ListImg
                  fluid={image.fluid}
                  alt={`${index} - ${data.title} - Case Study - Lattice Studios `}
                />
              </li>
            )
          }
        })}
      </ul>
    </React.Fragment>
  )
}

export default CaseStudyRoot
